import { A } from "@solidjs/router"
import { createSignal, onMount, Show } from "solid-js"
import { useLocalStore, User } from "@/context/StateContext"
import { supportsWebAuthn, supportsConditionalMediation, authDefaults, auth, regDefaults, register } from "@lo-fi/webauthn-local-client"
import { Cards } from "./Cards"
import { v4 as uuidv4, validate as validateUUID } from "uuid"

export default function SignIn() {
  const LocalStore = useLocalStore()
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal("")
  const [authController, setAuthController] = createSignal<AbortController | null>(null)
  const [registering, setRegistering] = createSignal(false)
  const [displayName, setDisplayName] = createSignal<string>("")

  onMount(() => {
    const storedId = localStorage.getItem("activeUserId")
    if (storedId) {
      LocalStore.setActiveUser(storedId)
    }
  })

  const handleRegister = async () => {
    setLoading(true)
    setError("")

    try {
      const userId = uuidv4()
      if (!validateUUID(userId)) {
        throw new Error("Failed to generate valid user ID")
      }

      const regResult = await register(regDefaults({
        relyingPartyName: "Stuff",
        user: {
          id: new TextEncoder().encode(userId),
          name: userId,
          displayName: displayName() || userId
        }
      }))

      LocalStore.setActiveUser(userId, displayName() || undefined)
    } catch (err: any) {
      setError(err.message || "Registration failed")
    } finally {
      setLoading(false)
    }
  }

  const startAuth = async (withInput = false) => {
    authController()?.abort()
    const controller = new AbortController()
    setAuthController(controller)

    setLoading(true)
    setError("")

    try {
      const options = {
        ...authDefaults(),
        mediation: withInput ? "conditional" : "optional",
        timeout: 60000,
        userVerification: "preferred",
        signal: controller.signal,
      }

      const authResult = await navigator.credentials.get({
        publicKey: {
          ...options,
          challenge: new Uint8Array(32),
          rpId: window.location.hostname,
        }
      }) as PublicKeyCredential

      if (!authResult) throw new Error("No credential returned")

      const response = authResult.response as AuthenticatorAssertionResponse
      const userId = new TextDecoder().decode(response.userHandle)

      if (!validateUUID(userId)) throw new Error("Invalid user ID")

      LocalStore.setActiveUser(userId)
    } catch (err: any) {
      console.error('Detailed auth error:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        error: err
      })

      if (err.name === "AbortError") return

      if (err.name === "NotAllowedError") {
        setError("Authentication was cancelled")
      } else if (err.name === "SecurityError") {
        setError("No matching passkey found - try registering a new one")
      } else {
        setError(`Authentication failed: ${err.message || err.name}`)
      }
    } finally {
      setLoading(false)
      setAuthController(null)
    }
  }

  const startRegistration = () => {
    authController()?.abort()
    setRegistering(true)
    setError("")
  }

  return (
    <div class="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50 dark:bg-gray-900">
      <div class="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
        <h1 class="text-2xl font-bold text-center text-gray-900 dark:text-white">Welcome</h1>

        <Show when={supportsWebAuthn} fallback={
          <div class="text-red-500 text-center">
            WebAuthn is not supported in this browser.<br />Please use a modern browser.
          </div>
        }>
          <div class="space-y-4">
            <Show
              when={registering()}
              fallback={
                <div class="space-y-4">
                  <Show
                    when={supportsConditionalMediation}
                    fallback={
                      <button
                        onClick={() => startAuth(false)}
                        disabled={loading()}
                        class="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                      >
                        {loading() ? "Authenticating..." : "Sign in with passkey"}
                      </button>
                    }
                  >
                    <div class="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Click to sign in with passkey"
                        class="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                        autocomplete="username webauthn"
                        readonly
                        onClick={() => startAuth(true)}
                      />
                      <p class="text-sm text-gray-500 dark:text-gray-400 text-center">
                        Click the input field above to use your passkey
                      </p>
                    </div>
                  </Show>

                  <div class="relative">
                    <div class="absolute inset-0 flex items-center">
                      <div class="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div class="relative flex justify-center text-sm">
                      <span class="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or</span>
                    </div>
                  </div>

                  <button
                    onClick={startRegistration}
                    class="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-gray-200"
                  >
                    Register new passkey
                  </button>
                </div>
              }
            >
              <div class="space-y-4">
                <input
                  type="text"
                  placeholder="Enter a name for this passkey (optional)"
                  value={displayName()}
                  onInput={(e) => setDisplayName(e.currentTarget.value)}
                  class="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
                />
                <div class="flex gap-3">
                  <button
                    onClick={() => {
                      setRegistering(false)
                      setError("")
                      setDisplayName("")
                    }}
                    class="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleRegister}
                    disabled={loading()}
                    class="flex-1 p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {loading() ? "Registering..." : "Register passkey"}
                  </button>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 text-center">
                  This name will help you identify this passkey on this device
                </p>
              </div>
            </Show>

            <Show when={error()}>
              <div class="text-red-500 text-center text-sm">{error()}</div>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  )
}
